<script>
   /*************************************************************/
   /* PlotAcceptanes for DDS-SIMCA results                      */
   /* ----------------------------                              */
   /* like plotDistance but with extra options.                 */
   /*************************************************************/

   import { Lines, ColormapLegend } from 'svelte-plots-basic/2d';
   import { pcares_get } from '@mdatools-apps/wasm/pca.js';
   import { csvRow, downloadCSV } from '../utils.js';

   import { getColorGroups, getDistanceCoords, getDistanceAxisLabel, getDistanceLimitCoordinates, MAX_LABEL_SYMBOLS, MARKERS, MARGINS } from './Defaults.js';
   import PlotSeries from './PlotSeries.svelte';
   import Axes from './Axes.svelte';
   import { plotSize, legendNames } from './layout.js';

   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();


   // get the parameters needed for the plot
   // get the parameters needed for the plot
   const data = $derived(props && props.data ? props.data : null);
   const res = $derived(props && props.res ? props.res : null);
   const resobj = $derived(res && params.resID ? res[params.resID] : null);
   const dataset = $derived(res && params.resID ? data[params.resID] : null);
   const model = $derived(props && props.model ? props.model : null);

   // constant plot parameters
   const LINE_COLOR = '#909090';    // color of the limits lines
   const NPOINTS = 100;             // number of points in limit lines
   const fileName = 'plot-acceptance'; // file name for the plot

   // get specific type of limits
   let N = $derived(params.limType === 'classic' ?
      {h: resobj['cNh'], q: resobj['cNq']} :
      {h: resobj['rNh'], q: resobj['rNq']}
   );

   let u0 = $derived(params.limType === 'classic' ?
      {h: resobj['ch0'], q: resobj['cq0']} :
      {h: resobj['rh0'], q: resobj['rq0']}
   );

   // get limit values for specific number of components
   let Na = $derived({h: N.h[params.ncomp - 1], q: N.q[params.ncomp - 1]});
   let u0a = $derived({h: u0.h[params.ncomp - 1], q: u0.q[params.ncomp - 1]});

   // get conf limits
   let ecl = $derived(params.alpha ? 1 - params.alpha/100 : 0.95);
   let ocl = $derived(params.gamma ? 1 - params.gamma/100 : 0.99);

   // get coordinates for limits
   let els = $derived(getDistanceLimitCoordinates(ecl, Na.h, Na.q, params.transType));
   let ols = $derived(model ? getDistanceLimitCoordinates(Math.pow(ocl, 1/model.nrows), Na.h, Na.q, params.transType) : null);

   // the outlier boundary is not shown for a new set, nor for a test set unless its subset is
   // the members
   let olsShown = $derived(ols &&
      (params.resID !== "test" || (params.subset && params.subset === "members")) &&
      params.resID && params.resID !== 'new');

   // reactive expression to compute colorby parameters
   let h = $derived(resobj ? pcares_get(resobj, "hdist", params.ncomp): null);
   let q = $derived(resobj ? pcares_get(resobj, "qdist", params.ncomp): null);


   // compute coordinates and labels
   let xc = $derived(getDistanceCoords(h, Na.h, u0a.h, params.transType));
   let yc = $derived(getDistanceCoords(q, Na.q, u0a.q, params.transType));
   let labels = $derived(resobj ? resobj.objlabels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)) : null);


   // derive plot parameters
   let title = $derived(params.title ? params.title : 'Distances (A = ' + params.ncomp + ')');
   let xLabel = $derived(getDistanceAxisLabel('h', params.transType));
   let yLabel = $derived(getDistanceAxisLabel('q', params.transType));

   // ranges of values to define coordinate limits
   let xRange = $state(), yRange = $state();

   // reactive expression to compute colorby parameters
   let col = $derived(getColorGroups(dataset, resobj, params.colorBy, params.colorByVar, params.resID, params.colorByObj));

   // the drawn size of the plot, for the class names cut to fit their legend cells
   let size = $state([0, 0]);
   const legendLabels = $derived(col?.byClass ? legendNames(col.labels, size, MARGINS, true) : col?.labels);

   let ind = $derived(params.colorByObj ? params.colorByObj.ind : null);

   let limX = $derived.by(() => {
      if (!ols || !xRange) return [0, 1];
      const xMaxTot = Math.max(xRange[1], ols.x.v[NPOINTS - 1]);
      return [-0.02 * (xMaxTot - xRange[0]), 1.05 * xMaxTot];
   });


   let limY = $derived.by(() => {
      if (!ols || !yRange) return [0, 1];
      const yMaxTot = Math.max(yRange[1], ols.y.v[0]);
      return [-0.02 * (yMaxTot - yRange[0]), (1.05 + 0.10 * (params.colorBy !== 'none') + 0.05 * (params.showLabels)) * yMaxTot];
   });

   // method for creating CSV version of the plot data
   function downloadCSVCallback() {
      // 'data' is a map keyed by result id: the first set's separator
      const { sep, dec } = Object.values(data)[0];

      // elements of the first (main) subset
      let l = resobj.objlabels;
      let x = Array.from(xc.values.v);
      let y = Array.from(yc.values.v);
      let s = Array(x.length).fill(params.resID);
      let c = resobj.classes && resobj.classes.length === x.length ? resobj.classes : Array(x.length).fill("");
      let r = params.colorByObj ? params.colorByObj.values : Array(x.length).fill("");

      if (els) {
         const ne = els.x.length;
         s = [...s, ...Array(ne).fill('extreme boundary')];
         l = [...l, ...Array(ne).fill('')];
         c = [...c, ...Array(ne).fill('')];
         r = [...r, ...Array(ne).fill('')];
         x = [...x, ...Array.from(els.x.v)];
         y = [...y, ...Array.from(els.y.v)];
      }

      if (olsShown) {
         const no = ols.x.length;
         s = [...s, ...Array(no).fill('outlier boundary')];
         l = [...l, ...Array(no).fill('')];
         c = [...c, ...Array(no).fill('')];
         r = [...r, ...Array(no).fill('')];
         x = [...x, ...Array.from(ols.x.v)];
         y = [...y, ...Array.from(ols.y.v)];
      }


      let out = csvRow(['Set', 'Labels', 'Classes', 'Roles', xLabel, yLabel], sep, dec);
      for (let i = 0; i < x.length; i++) {
         out += csvRow([s[i], l[i], c[i], r[i], x[i], y[i]], sep, dec);
      }

      downloadCSV(out, fileName);
   }

</script>

{#if xc && yc}
<!-- 'display: contents' leaves the layout to Axes -->
<div style="display: contents" {@attach plotSize(v => size = v)}>
<Axes {title} {limX} {limY} {xLabel} {yLabel} {downloadCSVCallback} {fileName}>

   <!-- limits  -->
   {#if els }
   <Lines xValues={els.x} yValues={els.y} lineColor={LINE_COLOR} lineType={2} />
   {/if}

   {#if olsShown}
   <Lines xValues={ols.x} yValues={ols.y} lineColor={LINE_COLOR} lineType={3} />
   {/if}

   <!-- data points 1 -->
   <PlotSeries
      labels={labels}
      {selected}
      {callbacks}
      xValues={xc.values}
      yValues={yc.values}
      plotType='p'
      showLabels={params.showLabels}
      marker={MARKERS[params.resID]}
      colors={col.colors}
      filled={col.filled}
      {ind}
      bind:xRange={xRange}
      bind:yRange={yRange}
   />

   <!-- color by legend-->
   {#if col && col.showBar}
   <ColormapLegend show={col.breaks !== null} colmap={col.map} breaks={col.breaks} labels={legendLabels}/>
   {/if}
</Axes>
</div>
{/if}
